import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react'
import { Button } from './button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { PlanLimitNotice } from '@/components/features/entitlements/PlanLimitNotice'
import { AI_MAX_INSTRUCTION_LENGTH, type AiFieldKind, type AiTextFormat } from '@/lib/sdk-official/resources/ai'

export type { AiFieldKind }

/** True when the field has something worth improving (HTML tags alone do not count). */
const hasText = (text: string, format: AiTextFormat) =>
  (format === 'html' ? text.replace(/<[^>]*>/g, '') : text).trim().length > 0

/**
 * Read-only render of editor HTML. Going through the editor schema drops anything the editor
 * itself would not keep (scripts, unknown tags), so the model's answer is never injected raw.
 */
function HtmlPreview({ html }: { html: string }) {
  const editor = useEditor({
    editable: false,
    extensions: [StarterKit, Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-purple-600 underline' } })],
    content: html,
    editorProps: { attributes: { class: 'prose prose-sm max-w-none focus:outline-none' } }
  })
  React.useEffect(() => {
    if (editor && editor.getHTML() !== html) editor.commands.setContent(html)
  }, [editor, html])
  return <EditorContent editor={editor} />
}

function TextPreview({ text, format }: { text: string; format: AiTextFormat }) {
  return format === 'html'
    ? <HtmlPreview html={text} />
    : <p className="whitespace-pre-wrap break-words text-sm text-gray-800">{text}</p>
}

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  text: string
  format: AiTextFormat
  fieldKind: AiFieldKind
  onApply: (text: string) => void
  /** The field's own limit: an answer longer than this cannot be used. */
  maxLength?: number
}

/**
 * F-105: before and after side by side (stacked on a phone). Nothing is saved here: "Use this
 * text" only puts the answer in the field, and the form is saved as usual.
 */
export function ImproveWithAiDialog({ open, onOpenChange, text, format, fieldKind, onApply, maxLength }: DialogProps) {
  const { t } = useTranslation('common')
  const [instruction, setInstruction] = React.useState('')
  const [result, setResult] = React.useState<{ text: string; changed: boolean } | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  /* F-34: the error is the community's monthly AI quota (a 429 without Retry-After): waiting will not help, the plan does. */
  const [quotaHit, setQuotaHit] = React.useState(false)
  /* SEC-187: after a 429, the run button waits out `Retry-After` (seconds left, counted down). */
  const [waitSeconds, setWaitSeconds] = React.useState(0)
  React.useEffect(() => {
    if (waitSeconds <= 0) return
    const timer = window.setTimeout(() => setWaitSeconds((s) => Math.max(0, s - 1)), 1000)
    return () => window.clearTimeout(timer)
  }, [waitSeconds])
  // A reply that arrives after the dialog closed (or after a newer request) is dropped.
  const requestId = React.useRef(0)

  React.useEffect(() => {
    if (!open) return
    requestId.current += 1
    setInstruction('')
    setResult(null)
    setError(null)
    setQuotaHit(false)
    setBusy(false)
  }, [open])

  const improve = async () => {
    const id = ++requestId.current
    setBusy(true)
    setError(null)
    setQuotaHit(false)
    try {
      const data = await sdk.ai.improveText({ text, fieldKind, format, ...(instruction.trim() && { instruction: instruction.trim() }) })
      if (id === requestId.current) setResult({ text: data.text, changed: data.changed })
    } catch (err) {
      if (id === requestId.current) {
        const { message, status } = apiErrorMessage(err)
        if (status === 429) {
          // The brake (SEC-187) sends Retry-After; the community's monthly quota (F-34) does not, and waiting will not help
          const retryAfter = Number((err as { retryAfter?: number })?.retryAfter)
          if (Number.isFinite(retryAfter) && retryAfter > 0) setWaitSeconds(Math.min(Math.ceil(retryAfter), 3600))
          else setQuotaHit(true)
          setError(message || t('aiImprove.rateLimited'))
        } else {
          setError(message || t('aiImprove.failed'))
        }
      }
    } finally {
      if (id === requestId.current) setBusy(false)
    }
  }

  const tooLong = Boolean(result && maxLength && maxLength > 0 && result.text.length > maxLength)

  const close = () => {
    requestId.current += 1
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-4xl overflow-y-auto p-4 sm:p-6" data-testid="improve-ai-dialog">
        <div className="min-w-0 space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />{t('aiImprove.title')}</DialogTitle>
            <DialogDescription>{t('aiImprove.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <label htmlFor="improve-ai-instruction" className="text-sm font-medium text-gray-700">{t('aiImprove.instructionLabel')}</label>
            {/* A bare textarea: `Textarea` imports this file (it renders the button). */}
            <textarea id="improve-ai-instruction" rows={2}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 disabled:opacity-50" maxLength={AI_MAX_INSTRUCTION_LENGTH} value={instruction} disabled={busy}
              placeholder={t('aiImprove.instructionPlaceholder')} onChange={(e) => setInstruction(e.target.value)} />
          </div>

          <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
            <section className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t('aiImprove.before')}</h3>
              <div className="max-h-[40vh] overflow-y-auto"><TextPreview text={text} format={format} /></div>
            </section>
            <section className="min-w-0 rounded-xl border border-primary-200 bg-white p-3" data-testid="improve-ai-after">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">{t('aiImprove.after')}</h3>
              <div className="max-h-[40vh] overflow-y-auto">
                {busy ? (
                  <p role="status" className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />{t('aiImprove.working')}</p>
                ) : error && quotaHit ? (
                  <PlanLimitNotice message={error} upgradeHint={t('aiImprove.quotaHint')} testId="improve-ai-quota" />
                ) : error ? (
                  <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>
                ) : result ? (
                  <>
                    {tooLong && <p role="alert" className="mb-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">{t('aiImprove.tooLongForField', { max: maxLength })}</p>}
                    {!result.changed && <p className="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">{t('aiImprove.unchanged')}</p>}
                    <TextPreview text={result.text} format={format} />
                  </>
                ) : (
                  <p className="text-sm text-gray-500">{t('aiImprove.afterHint')}</p>
                )}
              </div>
            </section>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={close} data-testid="improve-ai-discard">{t('aiImprove.discard')}</Button>
            <Button type="button" variant={result ? 'outline' : 'secondary'} className="w-full sm:w-auto" disabled={busy || waitSeconds > 0 || quotaHit} onClick={() => void improve()} data-testid="improve-ai-run">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              {waitSeconds > 0 ? t('aiImprove.waitSeconds', { count: waitSeconds }) : result || error ? t('aiImprove.again') : t('aiImprove.run')}
            </Button>
            {result && (
              <Button type="button" variant="secondary" className="w-full sm:w-auto" disabled={busy || tooLong}
                onClick={() => { onApply(result.text); close() }} data-testid="improve-ai-apply">
                {t('aiImprove.apply')}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ButtonProps {
  /** Read at click time, so the dialog always gets the field's current text. */
  getText: () => string
  /** Whether the field has text right now (drives the disabled state and its hint). */
  empty: boolean
  format: AiTextFormat
  fieldKind: AiFieldKind
  onApply: (text: string) => void
  maxLength?: number
  disabled?: boolean
  /** Icon only, for one-line fields. */
  compact?: boolean
  className?: string
}

/** F-105: the "Improve with AI" icon button plus its dialog. */
export function ImproveWithAiButton({ getText, empty, format, fieldKind, onApply, maxLength, disabled, compact, className }: ButtonProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = React.useState(false)
  const [snapshot, setSnapshot] = React.useState('')
  const off = disabled || empty
  return (
    <>
      {/* The hint lives on a wrapper: a disabled button does not get hover events in every browser. */}
      <span title={empty ? t('aiImprove.emptyHint') : t('aiImprove.button')} className={cn('inline-flex', className)}>
        <Button type="button" variant="ghost" size="sm" disabled={off} aria-label={t('aiImprove.button')} data-testid="improve-ai-button"
          className="h-8 gap-1 px-2 text-primary hover:text-primary"
          onClick={() => { setSnapshot(getText()); setOpen(true) }}>
          <Sparkles className="h-4 w-4" />
          {!compact && <span className="hidden text-xs sm:inline">{t('aiImprove.short')}</span>}
        </Button>
      </span>
      <ImproveWithAiDialog open={open} onOpenChange={setOpen} text={snapshot} format={format} fieldKind={fieldKind} onApply={onApply} maxLength={maxLength} />
    </>
  )
}

export { hasText as hasImprovableText }

/** Puts text in a (controlled or not) textarea so React's `onChange` fires as if it was typed. */
const fillTextarea = (el: HTMLTextAreaElement, text: string) => {
  Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set?.call(el, text)
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.focus()
}

interface AddonProps {
  target: React.RefObject<HTMLTextAreaElement>
  /** The textarea's `value` prop, only to re-check emptiness when the parent changes it. */
  value: unknown
  fieldKind: AiFieldKind
  maxLength?: number
  disabled?: boolean
  compact?: boolean
  className?: string
}

/** F-105: the "Improve with AI" button for a plain-text textarea (`Textarea`, `AutoTextarea`). */
export function TextareaAiAddon({ target, value, fieldKind, maxLength, disabled, compact, className }: AddonProps) {
  const [empty, setEmpty] = React.useState(true)
  React.useEffect(() => {
    const el = target.current
    if (!el) return
    const check = () => setEmpty(!hasText(el.value, 'plain'))
    check()
    el.addEventListener('input', check)
    return () => el.removeEventListener('input', check)
  }, [target, value])
  return (
    <ImproveWithAiButton
      className={className}
      format="plain"
      fieldKind={fieldKind}
      empty={empty}
      disabled={disabled}
      compact={compact}
      maxLength={maxLength}
      getText={() => target.current?.value ?? ''}
      onApply={(text) => { if (target.current) fillTextarea(target.current, text) }}
    />
  )
}
